// src/components/auction/panoramicaEngine.js
// Motore "Panoramica squadra": sceglie il modulo/undici migliore per la
// PROSSIMA giornata (non solo per un modulo scelto a mano, come in
// AuctionFormationTab) e spiega, giocatore per giocatore, i dubbi da
// conoscere prima di schierarlo. Riusa gli stessi building block già scritti
// per Formazione/Scambi — nessun nuovo modello, solo una lettura più
// aggressiva degli stessi segnali (qui la posta in gioco è UNA giornata sola,
// non un valore stagionale, quindi le disponibilità pesano molto di più).
import {
  buildClassicBoard,
  buildMantraBoard,
  CLASSIC_OPTIONS,
  MANTRA_OPTIONS,
} from './AuctionFormationTab';
import { computeRecentFormDelta, getDisciplinaryRisk } from './auctionAnalysis';
import { ROLE_ORDER, ROLE_LABELS } from './auctionCommon';
import { getPrimaryRoleValue } from './auctionRosterUtils';
import { getUpcomingFixturesWithDifficulty } from '../../data/serieACalendar';

function v(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

// ── Regolamento di lega ─────────────────────────────────────────────────────
// Le voci facoltative del punto 7/10 del regolamento fantacalcio.it (vedi
// https://www.fantacalcio.it/regolamenti/leghe-private) cambiano da lega a
// lega: non c'è una tabella unica, quindi vanno chieste all'utente invece che
// assunte. I valori bonus/malus di base (gol, assist, cartellini...) restano
// invece quelli più diffusi, non ancora personalizzabili per lega.
export const DEFAULT_SCORING_RULES = {
  cleanSheet: true,
  defenseModifier: true,
  ownGoal: true,
  captain: false,
  decisiveGoal: false,
};

export function getAuctionScoringRules(auction) {
  return { ...DEFAULT_SCORING_RULES, ...(auction?.scoringRules || {}) };
}

const GOAL_BONUS = 3;
const ASSIST_BONUS = 1;
const GOAL_CONCEDED_MALUS = 1;
const CLEAN_SHEET_BONUS = 1;

// Modificatore di difesa, versione standard di Leghe Fantacalcio (la
// piattaforma permette di cambiare i valori del bonus, non la struttura):
// si attiva SOLO con almeno 4 difensori schierati; media dei voti in
// pagella (voto puro, senza bonus/malus) del portiere e dei 3 migliori
// difensori; bonus a fasce.
const DEF_MOD_MIN_DEFENDERS = 4;
const DEF_MOD_BANDS = [
  { min: 7, bonus: 6 },
  { min: 6.5, bonus: 3 },
  { min: 6, bonus: 1 },
];
// Deviazione standard della media di 4 voti nella singola giornata: la media
// stagionale dice dove "sta" il reparto, ma la giornata oscilla parecchio.
const DEF_MOD_AVG_SIGMA = 0.35;

// Probabilità di clean sheet per una squadra "media" su un turno "medio"
// (difficoltà calendario neutra): usata come riferimento zero del
// modificatore di difesa. In Serie A una squadra media tiene la porta
// inviolata in circa 1 partita su 3, non 1 su 2 — un valore più alto
// (usato in una versione precedente) rendeva il bonus quasi scontato ogni
// settimana anche a calendario neutro, troppo ottimista.
const NEUTRAL_CLEAN_SHEET_PROB = 0.32;

// Stima (0-1) di quanto la difesa avversaria regga, dal calendario: usata sia
// per l'imbattibilità portiere sia per il modificatore di difesa, che nella
// realtà dipendono entrambi da quanto la propria difesa tiene la porta
// inviolata. È una stima grezza, non un pronostico.
function estimateCleanSheetProb(calendarEntry) {
  const difficulty = calendarEntry?.difficulty;
  if (!Number.isFinite(difficulty)) return NEUTRAL_CLEAN_SHEET_PROB;
  return clamp(NEUTRAL_CLEAN_SHEET_PROB - (difficulty - 3) * 0.09, 0.08, 0.6);
}

export function getPurchaseName(purchase) {
  return purchase?.playerSnapshot?.name || purchase?.playerName || '—';
}

export function getPurchaseTeam(purchase) {
  return purchase?.playerSnapshot?.team || purchase?.playerTeam || '—';
}

function getAvailability(purchase, availabilityById) {
  if (!availabilityById) return null;
  const id = String(purchase?.playerId ?? purchase?.id ?? '');
  return typeof availabilityById.get === 'function' ? availabilityById.get(id) : availabilityById[id];
}

// Un flag "non disponibile" così netto (infortunato, o probabile formazione
// con percentuale vicina allo zero) non deve dipendere solo da una penalità
// di punteggio: se quel giocatore è comunque il più forte sulla carta nel suo
// ruolo, una penalità fissa potrebbe non bastare a scavalcarlo. Va escluso
// del tutto dal pool di scelta, non solo "sfavorito".
const UNAVAILABLE_PROB_THRESHOLD = 10;
const BENCH_RISK_PENALTY = 1.5;

function isUnavailableForSelection(purchase, availabilityById) {
  if (!availabilityById) return false;
  const id = String(purchase?.playerId ?? purchase?.id ?? '');
  const avail = typeof availabilityById.get === 'function' ? availabilityById.get(id) : availabilityById[id];
  if (!avail) return false;
  if (avail.injury) return true;
  const prob = Number(avail.lineup?.prob);
  if (avail.lineup && Number.isFinite(prob) && prob <= UNAVAILABLE_PROB_THRESHOLD) return true;
  return false;
}

// ── Scelta del modulo migliore ──────────────────────────────────────────────
// AuctionFormationTab lascia scegliere il modulo all'utente; qui invece si
// prova OGNI modulo disponibile per il rolesMode e si tiene quello che dà il
// punteggio titolari più alto (a parità, si preferisce un modulo completo,
// cioè senza slot vuoti, a uno che segna di più ma lascia buchi).
// Il confronto tra moduli usa la stessa proiezione di voto mostrata in pagina
// (estimatePlayerProjection) PIÙ il modificatore di difesa atteso del modulo:
// con il modificatore attivo, una difesa a 3 rinuncia al bonus (servono almeno
// 4 difensori), e questo deve pesare nella scelta del modulo.
export function pickBestFormationBoard({ roster = [], rolesMode = 'classic', ratingsById = null, availabilityById = null, rules = DEFAULT_SCORING_RULES }) {
  const options = rolesMode === 'mantra' ? MANTRA_OPTIONS : CLASSIC_OPTIONS;
  const adjustments = { ratingsById, availabilityById };

  const search = (candidateRoster) => {
    let best = null;
    let bestRank = -Infinity;

    options.forEach((formationKey) => {
      const board = rolesMode === 'mantra'
        ? buildMantraBoard(candidateRoster, formationKey, adjustments)
        : buildClassicBoard(candidateRoster, formationKey, adjustments);

      const starters = flattenBoardStarters(board, rolesMode);
      const projected = starters.reduce((acc, item) => {
        const { score } = estimatePlayerProjection({
          item, ratingsById, calendarEntry: defaultCalendarFor(getPurchaseTeam(item.purchase)), rules,
        });
        // Solo per il confronto tra moduli: un titolare che le probabili
        // danno in panchina rischia di non prendere voto.
        const avail = getAvailability(item.purchase, availabilityById);
        return acc + score - (avail?.lineup?.status === 'bench' ? BENCH_RISK_PENALTY : 0);
      }, 0);
      const defenseModifier = estimateDefenseModifier({ starters, ratingsById, rules });
      const totalScore = projected + defenseModifier.expected;
      // Un modulo completo vale sempre più di uno incompleto, a prescindere dal
      // punteggio: schierare 11 uomini viene prima di ottimizzare la qualità.
      const rank = (board.complete ? 1e6 : 0) + totalScore;

      if (rank > bestRank) {
        bestRank = rank;
        best = { board, formationKey, totalScore, complete: board.complete, defenseModifier };
      }
    });

    return best;
  };

  // Chi è segnalato indisponibile (infortunio, probabile formazione vicina
  // allo zero, o marcato a mano dall'utente) va escluso SEMPRE dal pool dei
  // titolari — anche se questo lascia il modulo incompleto in un ruolo.
  // Deliberatamente NESSUN fallback che lo faccia rientrare "per completare
  // la formazione": mostrare un giocatore che con certezza non gioca sarebbe
  // peggio di un undici scoperto in un ruolo, che almeno è onesto su cosa
  // manca in rosa.
  const available = roster.filter((p) => !isUnavailableForSelection(p, availabilityById));
  return search(available.length ? available : roster);
}

// Normalizza titolari Classic/Mantra in una lista piatta con etichetta di
// ruolo/reparto pronta per la UI, senza dover ramificare rolesMode ovunque.
export function flattenBoardStarters(board, rolesMode) {
  if (!board) return [];

  if (rolesMode === 'mantra') {
    const out = [];
    (board.rows || []).forEach((row) => {
      row.forEach((slot) => {
        if (slot.player) {
          out.push({ purchase: slot.player, roleLabel: slot.rowTitle || slot.rowKey, roleBucket: slot.rowKey });
        }
      });
    });
    return out;
  }

  return ROLE_ORDER.flatMap((role) =>
    (board.startingByRole?.[role] || []).map((purchase) => ({
      purchase,
      roleLabel: ROLE_LABELS[role] || role,
      roleBucket: role,
    }))
  );
}

// Righe pronte per una visualizzazione a campo (portiere → attacco, come
// preset.rows/ROLE_ORDER le espongono già): ogni riga è un reparto con i
// suoi titolari, senza un ordine orizzontale significativo al suo interno
// (si distribuiscono semplicemente a spaglio nella UI).
export function buildPitchRows(board, rolesMode) {
  if (!board) return [];

  if (rolesMode === 'mantra') {
    return (board.rows || [])
      .map((row) => ({
        key: row[0]?.rowKey,
        label: row[0]?.rowTitle,
        players: row.map((slot) => slot.player).filter(Boolean),
      }))
      .filter((r) => r.players.length);
  }

  return ROLE_ORDER
    .map((role) => ({ key: role, label: ROLE_LABELS[role] || role, players: board.startingByRole?.[role] || [] }))
    .filter((r) => r.players.length);
}

// Coarsening del bucket titolare (P/D/C/T/A per il Mantra, righe di
// buildPitchRows) sullo stesso 4-bucket P/D/C/A usato ovunque nell'app per
// classificare un ruolo mantra da stringa (getMantraBucket/getPrimaryRoleValue
// in auctionRosterUtils.js, es. Market/Squadre): T confluisce in C. Serve a
// confrontare un titolare con una riserva che magari è eleggibile solo su un
// sottoinsieme di ruoli — la comparazione resta "reparto", non "riga esatta".
function coarseBucket(roleBucket) {
  return roleBucket === 'T' ? 'C' : roleBucket;
}

// ── Proiezione voto/punteggio di un titolare per la PROSSIMA giornata ──────
// Stima, non un pronostico: voto medio stagionale + bonus attesi (gol/assist
// stimati dal ritmo stagionale, gol fatti/subiti dal ruolo) + un piccolo
// aggiustamento su forma recente e calendario. Le regole di lega (rules)
// decidono SE includere imbattibilità portiere e modificatore di difesa —
// sono le voci che cambiano di più da lega a lega (vedi getAuctionScoringRules).
export function estimatePlayerProjection({ item, ratingsById, calendarEntry, rules = DEFAULT_SCORING_RULES }) {
  const { purchase, roleBucket: rawBucket } = item;
  const roleBucket = coarseBucket(rawBucket);
  const snap = purchase?.playerSnapshot || purchase || {};
  const baseVote = v(snap.markavg) > 0 ? v(snap.markavg) : 6;
  const caps = Math.max(1, v(snap.caps) || 1);
  const form = ratingsById ? computeRecentFormDelta(purchase, ratingsById) : null;
  const formDelta = typeof form === 'number' ? form : v(form?.delta);

  const components = [];
  let bonus = 0;

  if (roleBucket === 'P') {
    if (rules.cleanSheet) {
      const csProb = estimateCleanSheetProb(calendarEntry);
      const value = Math.round(csProb * CLEAN_SHEET_BONUS * 10) / 10;
      if (value > 0) { bonus += value; components.push({ label: 'imbattibilità', value }); }
    }
    const concededRate = v(snap.gotgoals ?? snap.goalsConceded) / caps;
    bonus -= concededRate * GOAL_CONCEDED_MALUS;
  } else if (roleBucket === 'D') {
    // Il modificatore di difesa NON è del singolo difensore: dipende da
    // quanti difensori schiera il modulo (almeno 4) e dalla media del
    // reparto — è calcolato a livello di formazione in
    // estimateDefenseModifier, non qui.
    const attackValue = Math.round((v(snap.goals) / caps * GOAL_BONUS + v(snap.assists) / caps * ASSIST_BONUS) * 10) / 10;
    if (attackValue >= 0.1) { bonus += attackValue; components.push({ label: 'bonus attesi', value: attackValue }); }
  } else {
    const attackValue = Math.round((v(snap.goals) / caps * GOAL_BONUS + v(snap.assists) / caps * ASSIST_BONUS) * 10) / 10;
    if (attackValue >= 0.1) { bonus += attackValue; components.push({ label: 'bonus attesi', value: attackValue }); }
  }

  const formAdj = clamp(formDelta, -2, 2) * 0.15;
  const score = Math.round((baseVote + bonus + formAdj) * 10) / 10;

  return { score, baseVote: Math.round(baseVote * 10) / 10, components };
}

// ── Modificatore di difesa (a livello di FORMAZIONE) ────────────────────────
function normalCdf(x) {
  // Approssimazione di Abramowitz-Stegun (errore < 1e-7), sufficiente qui.
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
}

// Voto in pagella atteso (voto puro, niente bonus/malus) per la prossima
// giornata: media stagionale, un filo di forma recente e del calendario
// (contro un attacco forte i voti di portiere e difensori calano).
function expectedPureVote(purchase, ratingsById, calendarEntry) {
  const snap = purchase?.playerSnapshot || purchase || {};
  const base = v(snap.markavg) > 0 ? v(snap.markavg) : 6;
  const form = ratingsById ? computeRecentFormDelta(purchase, ratingsById) : null;
  const formDelta = typeof form === 'number' ? form : v(form?.delta);
  const difficulty = Number(calendarEntry?.difficulty);
  const calAdj = Number.isFinite(difficulty) ? -(difficulty - 3) * 0.1 : 0;
  return base + clamp(formDelta, -2, 2) * 0.1 + calAdj;
}

// Valore ATTESO del modificatore per un undici: si attiva solo con almeno 4
// difensori (con un 3-x-x è sempre 0), media portiere + 3 migliori difensori,
// e siccome la media della singola giornata oscilla, il bonus atteso è la
// somma delle fasce pesate per la probabilità di raggiungerle — così un
// reparto da 6.4 di media vale più di +1 (a volte arriva a 6.5) e meno di +3.
// starters: [{ purchase, roleBucket }] — calendarFor(team) → calendarEntry.
export function estimateDefenseModifier({ starters = [], ratingsById = null, rules = DEFAULT_SCORING_RULES, calendarFor = defaultCalendarFor }) {
  const goalkeepers = starters.filter((s) => coarseBucket(s.roleBucket) === 'P');
  const defenders = starters.filter((s) => coarseBucket(s.roleBucket) === 'D');
  const base = { enabled: Boolean(rules.defenseModifier), applies: false, defenders: defenders.length, expected: 0, average: null };
  if (!rules.defenseModifier) return base;
  if (defenders.length < DEF_MOD_MIN_DEFENDERS) {
    return { ...base, reason: `servono almeno ${DEF_MOD_MIN_DEFENDERS} difensori (ne schieri ${defenders.length})` };
  }
  if (!goalkeepers.length) return { ...base, reason: 'manca il portiere titolare' };

  const voteOf = (s) => expectedPureVote(s.purchase, ratingsById, calendarFor(getPurchaseTeam(s.purchase)));
  const bestDefenders = defenders.map(voteOf).sort((a, b) => b - a).slice(0, 3);
  const average = (voteOf(goalkeepers[0]) + bestDefenders.reduce((a, b) => a + b, 0)) / 4;

  let expected = 0;
  let prevProb = 0; // probabilità delle fasce più alte già contate
  DEF_MOD_BANDS.forEach((band) => {
    const probAtLeast = 1 - normalCdf((band.min - average) / DEF_MOD_AVG_SIGMA);
    expected += (probAtLeast - prevProb) * band.bonus;
    prevProb = probAtLeast;
  });

  return {
    ...base,
    applies: true,
    average: Math.round(average * 100) / 100,
    expected: Math.round(expected * 10) / 10,
  };
}

const calendarCache = new Map();
function defaultCalendarFor(team) {
  // Chiave col giorno: passata la giornata, il "prossimo avversario" cambia.
  const key = `${team}|${new Date().toDateString()}`;
  if (!calendarCache.has(key)) calendarCache.set(key, getUpcomingFixturesWithDifficulty(team, 1)[0] || null);
  return calendarCache.get(key);
}

// "Capitano": nessuna scelta manuale per ora, si prende chi ha la proiezione
// più alta tra i titolari (scelta più comune quando non si specifica). Regola
// classica del raddoppio: il punteggio sopra la sufficienza conta doppio.
// Il vice-capitano è la seconda proiezione: subentra solo se il capitano non
// prende voto, quindi il suo punteggio NON viene raddoppiato nella proiezione.
export function applyCaptainRule(startersWithProjection, rules = DEFAULT_SCORING_RULES) {
  if (!rules.captain || !startersWithProjection.length) {
    return startersWithProjection.map((s) => ({ ...s, isCaptain: false, isViceCaptain: false }));
  }
  const ranked = startersWithProjection
    .map((s, index) => ({ index, score: s.projection.score }))
    .sort((a, b) => b.score - a.score || a.index - b.index);
  const captainIndex = ranked[0]?.index;
  const viceIndex = ranked[1]?.index;
  return startersWithProjection.map((s, index) => {
    if (index === captainIndex) {
      const doubled = Math.round((6 + (s.projection.score - 6) * 2) * 10) / 10;
      return { ...s, isCaptain: true, isViceCaptain: false, projection: { ...s.projection, score: doubled, captainDoubled: true } };
    }
    return { ...s, isCaptain: false, isViceCaptain: index === viceIndex };
  });
}

// Pro e contro di un titolare (o di una riserva, stesso identico calcolo,
// stessa fonte dati — availabilityById è la mappa unica già usata per
// scegliere l'undici, mai il flag derivato solo per i titolari): ogni motivo
// porta un'etichetta di provenienza — Stat, Trend, Calendario, Disciplina o
// Disponibilità — così si vede da dove viene, non solo cosa dice. Non c'è
// riempitivo quando non emerge nulla: una sezione vuota è un'informazione
// valida ("niente di notevole"), non un bug da nascondere con una frase
// finta. Ogni lato è tenuto a un massimo di 2 voci per lasciare spazio a
// combinazioni diverse da giocatore a giocatore, invece di ripetere sempre
// gli stessi 2-3 segnali.
export function buildPlayerReasons({ item, ratingsById, calendarEntry, availabilityById = null }) {
  const { purchase, roleBucket: rawBucket } = item;
  const roleBucket = coarseBucket(rawBucket);
  const snap = purchase?.playerSnapshot || {};
  const caps = v(snap.caps);
  const pros = [];
  const cons = [];

  // Testi brevi (una frase corta, non un periodo) apposta: devono stare in
  // 2 righe nello spazio fisso della card (vedi .psq-reasons__item in
  // PanoramicaSquadraPage.css) senza essere tagliati.
  const avail = getAvailability(purchase, availabilityById);
  if (avail?.injury) {
    // Il motivo può essere una frase intera (fonte: descrizione FantaMaster) —
    // qui serve solo il "quando rientra" se disponibile, non il racconto
    // completo, per restare nello spazio fisso della card.
    const expectedReturn = String(avail.injury.expectedReturn || '').trim();
    cons.push({
      tag: 'Disponibilità',
      text: expectedReturn ? `Infortunato, rientro previsto: ${expectedReturn}.` : 'Infortunato.',
    });
  } else if (avail?.lineup?.status === 'bench') {
    cons.push({ tag: 'Disponibilità', text: 'Fuori dai titolari nelle probabili formazioni.' });
  } else if (avail?.lineup?.status === 'starter') {
    pros.push({ tag: 'Disponibilità', text: 'Titolare secondo le probabili formazioni.' });
  }

  const risk = getDisciplinaryRisk(purchase);
  if (risk?.level === 'high') {
    cons.push({ tag: 'Disciplina', text: `Diffidato (${risk.ycards} gialli): rischia lo stop.` });
  } else if (v(snap.ycards) >= 3) {
    cons.push({ tag: 'Disciplina', text: `Già ${v(snap.ycards)} ammonizioni in stagione.` });
  }

  const form = typeof purchase.formDelta === 'number' ? purchase.formDelta : (ratingsById ? computeRecentFormDelta(purchase, ratingsById)?.delta : null);
  if (typeof form === 'number' && form > 1) {
    pros.push({ tag: 'Trend', text: 'In crescita nelle ultime giornate.' });
  } else if (typeof form === 'number' && form < -1) {
    cons.push({ tag: 'Trend', text: 'In calo nelle ultime giornate.' });
  }

  if (calendarEntry && calendarEntry.difficulty >= 4) {
    cons.push({ tag: 'Calendario', text: `${calendarEntry.home ? 'Con' : 'Trasferta con'} ${calendarEntry.opponent}: avversario ostico.` });
  } else if (calendarEntry && calendarEntry.difficulty <= 2) {
    pros.push({ tag: 'Calendario', text: `${calendarEntry.home ? 'Con' : 'Trasferta con'} ${calendarEntry.opponent}: avversario abbordabile.` });
  }

  const markavg = v(snap.markavg);
  if (markavg >= 6.3) {
    pros.push({ tag: 'Stat', text: `Buona media voto (${markavg.toFixed(2)}).` });
  } else if (markavg > 0 && markavg < 5.8) {
    cons.push({ tag: 'Stat', text: `Media voto bassa (${markavg.toFixed(2)}).` });
  }

  if (roleBucket === 'P') {
    const saves = v(snap.savedPenalties ?? snap.penaltiesSaved ?? snap.penalties_saved ?? snap.saves);
    if (saves >= 1) {
      pros.push({ tag: 'Stat', text: `${saves} rigori parati in stagione.` });
    }
  } else {
    const goals = v(snap.goals);
    const assists = v(snap.assists);
    if (roleBucket === 'A' && goals >= 6) {
      pros.push({ tag: 'Stat', text: `${goals} gol: tra i più prolifici della rosa.` });
    } else if (goals + assists >= 3) {
      pros.push({ tag: 'Stat', text: `Buon apporto: ${goals} gol e ${assists} assist.` });
    } else if (caps >= 3 && goals + assists === 0) {
      cons.push({ tag: 'Stat', text: `${caps} presenze, ancora a secco di bonus.` });
    }
    if (v(snap.penalties) >= 2) {
      pros.push({ tag: 'Stat', text: `Rigorista: ${v(snap.penalties)} gol su rigore.` });
    }
  }

  const hasConcern = cons.length > 0;
  const concernSeverity = cons.some((c) => c.tag === 'Disponibilità') ? 'high' : (hasConcern ? 'medium' : 'good');

  return { pros: pros.slice(0, 2), cons: cons.slice(0, 2), hasConcern, concernSeverity };
}

// ── Radar "prossime giornate" per tutta la rosa (non solo i titolari) ──────
const RETURN_MONTHS = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];
// Giorno del mese usato per "inizio/metà/fine": senza qualificatore ("da
// ottobre", "non prima di ottobre") si prende il primo del mese.
const RETURN_QUALIFIER_DAY = { inizio: 7, meta: 15, fine: 25 };

// expectedReturn arriva come "[inizio|meta|fine] <mese>" (vedi
// extractExpectedReturn in injuriesService.js): qui diventa una data stimata,
// per capire quali delle prossime partite salterà. Un mese più indietro di
// quello precedente all'attuale si intende dell'anno dopo (stagione a cavallo
// d'anno: "febbraio" letto a ottobre è febbraio prossimo).
export function parseExpectedReturnDate(expectedReturn, now = Date.now()) {
  const text = String(expectedReturn || '').toLowerCase().trim();
  if (!text) return null;
  const parts = text.split(/\s+/);
  const monthIndex = RETURN_MONTHS.indexOf(parts[parts.length - 1]);
  if (monthIndex < 0) return null;
  const day = RETURN_QUALIFIER_DAY[parts.length > 1 ? parts[0].replace('à', 'a') : ''] || 1;
  const today = new Date(now);
  let year = today.getFullYear();
  if (monthIndex < today.getMonth() - 1) year += 1;
  return new Date(year, monthIndex, day);
}

// Stato di ogni partita per un infortunato: 'out' se cade prima del rientro
// stimato, 'doubt' se il rientro è sconosciuto (o è proprio a ridosso), null
// se dovrebbe essere già disponibile. Ritorna anche la prima partita utile.
function annotateInjuryFixtures(fixtures, injury, now) {
  const returnDate = parseExpectedReturnDate(injury.expectedReturn, now);
  let returnFixture = null;
  const annotated = fixtures.map((f, i) => {
    const fxTime = new Date(`${f.date}T12:00:00`).getTime();
    let status = null;
    if (!returnDate) {
      // Nessuna stima: la prossima la salta quasi certamente, le altre in dubbio.
      status = i === 0 ? 'out' : 'doubt';
    } else if (fxTime < returnDate.getTime()) {
      status = 'out';
    } else if (!returnFixture) {
      returnFixture = f;
      status = 'doubt'; // primo turno dopo il rientro: probabile panchina/minutaggio ridotto
    }
    return { ...f, status };
  });
  const missedCount = annotated.filter((f) => f.status === 'out').length;
  const returnInPast = returnDate && returnDate.getTime() < now;
  return { fixtures: annotated, returnDate, returnFixture, missedCount, returnInPast };
}

export function buildRosterOutlook({ rosterPurchases = [], ratingsById = null, availabilityById = null, count = 4, now = Date.now() }) {
  return rosterPurchases.map((purchase) => {
    const team = getPurchaseTeam(purchase);
    let fixtures = getUpcomingFixturesWithDifficulty(team, count, now);
    const form = ratingsById ? computeRecentFormDelta(purchase, ratingsById) : null;
    const avail = getAvailability(purchase, availabilityById);
    const risk = getDisciplinaryRisk(purchase);
    const avgDifficulty = fixtures.length
      ? fixtures.reduce((acc, f) => acc + f.difficulty, 0) / fixtures.length
      : null;

    let injury = null;
    if (avail?.injury) {
      const annotated = annotateInjuryFixtures(fixtures, avail.injury, now);
      fixtures = annotated.fixtures;
      injury = {
        reason: avail.injury.reason || '',
        expectedReturn: avail.injury.expectedReturn || null,
        returnDate: annotated.returnDate,
        returnFixture: annotated.returnFixture,
        missedCount: annotated.missedCount,
        returnInPast: annotated.returnInPast,
      };
    }

    let tag = { tone: 'neutral', text: 'Turno tranquillo' };
    if (injury) {
      tag = injury.returnInPast
        ? { tone: 'warn', text: 'Rientro imminente' }
        : { tone: 'bad', text: 'Infortunato' };
    } else if (avail?.lineup?.status === 'bench') {
      tag = { tone: 'warn', text: 'Dubbio dalle probabili' };
    } else if (risk?.level === 'high') {
      tag = { tone: 'warn', text: 'A rischio squalifica' };
    } else if (form && form.delta > 1) {
      tag = { tone: 'good', text: 'Trend positivo, sfruttalo' };
    } else if (form && form.delta < -1) {
      tag = { tone: 'bad', text: 'Rendimento in calo' };
    } else if (avgDifficulty != null && avgDifficulty >= 3.6) {
      tag = { tone: 'warn', text: 'Calendario duro in arrivo' };
    } else if (avgDifficulty != null && avgDifficulty <= 2.2) {
      tag = { tone: 'good', text: 'Calendario favorevole' };
    }

    return {
      purchase,
      fixtures,
      formDelta: form?.delta ?? null,
      availability: avail || null,
      injury,
      disciplinaryRisk: risk,
      avgDifficulty,
      tag,
    };
  });
}

// ── Il resto della rosa: pro/contro + confronto col titolare del reparto ──
// Stessa lettura dei titolari (buildPlayerReasons/estimatePlayerProjection)
// applicata a chi non gioca, con in più il confronto diretto contro il
// titolare più debole del suo stesso reparto — è quello il paragone che
// spiega "perché non gioca", non un confronto contro il migliore.
// Il bucket di una riserva viene da getPrimaryRoleValue (auctionRosterUtils),
// la stessa funzione usata ovunque nell'app per classificare un ruolo
// Classic/Mantra da stringa — mai un bucket "inventato" qui.
export function buildBenchEntries({ roster = [], starters = [], rolesMode = 'classic', ratingsById = null, availabilityById = null, calendarByTeam = null, rules = DEFAULT_SCORING_RULES }) {
  const starterIds = new Set(starters.map((s) => String(s.purchase.playerId ?? s.purchase.id)));
  const bench = roster.filter((p) => !starterIds.has(String(p.playerId ?? p.id)));

  const weakestStarterByBucket = new Map();
  starters.forEach((s) => {
    const bucket = coarseBucket(s.roleBucket);
    const current = weakestStarterByBucket.get(bucket);
    if (!current || s.projection.score < current.projection.score) {
      weakestStarterByBucket.set(bucket, s);
    }
  });

  return bench.map((purchase) => {
    const roleValue = purchase?.playerSnapshot?.role || purchase?.role || 'A';
    const roleBucket = getPrimaryRoleValue(roleValue, rolesMode);
    const team = getPurchaseTeam(purchase);
    const calendarEntry = calendarByTeam
      ? calendarByTeam.get(team) || null
      : getUpcomingFixturesWithDifficulty(team, 1)[0] || null;
    const item = { purchase, roleBucket };
    const projection = estimatePlayerProjection({ item, ratingsById, calendarEntry, rules });
    const reasons = buildPlayerReasons({ item, ratingsById, calendarEntry, availabilityById });
    const vsStarter = weakestStarterByBucket.get(roleBucket) || null;

    return {
      purchase,
      roleBucket,
      calendarEntry,
      projection,
      reasons,
      vsStarter: vsStarter ? {
        name: getPurchaseName(vsStarter.purchase),
        score: vsStarter.projection.score,
        delta: Math.round((projection.score - vsStarter.projection.score) * 10) / 10,
      } : null,
    };
  });
}
